import { useEffect, useMemo } from 'react';
import Grid from '@mui/material/Grid2';

import { PolkSalesWorkflowSet } from '../../types/Polk';

import { Metric } from '../../types/Analytics/Metric';
import { setupMetricChange } from '../../components/Metrics/MetricCard';
import { useAttributionPageContext } from '../../hooks/useAttributionPage';
import MetricCard from '../../components/Metrics/MetricCard';

export default function PolkMatchbackMetrics() {
    const {
        polkSalesWorkflowSets,
        isLoadingPolkSalesWorkflowSets,
        previousPolkSalesWorkflowSets,
        isLoadingPreviousPolkSalesWorkflowSets,

        performances,
        isFetchingPerformances,
        previousPerformances,
        isFetchingPreviousPerformances,

        attributionMetrics,
        setAttributionMetrics,

        amcReachAndFrequencyList,
        isFetchingAmcReachAndFrequencyList,

        previousAmcReachAndFrequencyList,
        isFetchingPreviousAmcReachAndFrequencyList,
    } = useAttributionPageContext();

    function getSumTotalCampaignPerformanceMetric(_metrics: any[], key: string): number {
        return _metrics.map((_metric: any) => _metric?.[key] ?? 0).reduce((_total, _subtotal) => _total + _subtotal, 0);
    }

    const getMetricByPolkSalesWorkflowSet = (
        dataSet: 'polk_sales' | 'polk_opportunities',
        target: string,
        format: 'number' | 'dollar' | 'decimal' | 'percent'
    ): Metric => {
        let _metric: Metric = {
            format: format,
            value: 0,
        };

        switch (dataSet) {
            case 'polk_sales':
                if (polkSalesWorkflowSets && polkSalesWorkflowSets.length > 0) {
                    _metric.value = getSumTotalCampaignPerformanceMetric(
                        polkSalesWorkflowSets.flatMap(
                            (workflowSet: PolkSalesWorkflowSet) => workflowSet.salesWorkflow?.workflowResults ?? []
                        ),
                        target
                    );
                }

                if (previousPolkSalesWorkflowSets && previousPolkSalesWorkflowSets.length > 0) {
                    _metric.previousValue = getSumTotalCampaignPerformanceMetric(
                        previousPolkSalesWorkflowSets.flatMap(
                            (workflowSet: PolkSalesWorkflowSet) => workflowSet.salesWorkflow?.workflowResults ?? []
                        ),
                        target
                    );
                }
                break;

            case 'polk_opportunities':
                if (polkSalesWorkflowSets && polkSalesWorkflowSets.length > 0) {
                    _metric.value = getSumTotalCampaignPerformanceMetric(
                        polkSalesWorkflowSets.flatMap(
                            (workflowSet: PolkSalesWorkflowSet) =>
                                workflowSet.opportunityWorkflow?.workflowResults ?? []
                        ),
                        target
                    );
                }

                if (previousPolkSalesWorkflowSets && previousPolkSalesWorkflowSets.length > 0) {
                    _metric.previousValue = getSumTotalCampaignPerformanceMetric(
                        previousPolkSalesWorkflowSets.flatMap(
                            (workflowSet: PolkSalesWorkflowSet) =>
                                workflowSet.opportunityWorkflow?.workflowResults ?? []
                        ),
                        target
                    );
                }
                break;
        }

        _metric = setupMetricChange(_metric);

        return _metric;
    };

    function getMetricByCampaignPerformance(
        target: string,
        format: 'number' | 'dollar' | 'decimal' | 'percent'
    ): Metric {
        let _metric: Metric = {
            format: format,
            value: 0,
        };

        if (performances.length > 0) {
            _metric.value = getSumTotalCampaignPerformanceMetric(performances, target);
        }

        if (previousPerformances.length > 0) {
            _metric.previousValue = getSumTotalCampaignPerformanceMetric(previousPerformances, target);
        }

        _metric = setupMetricChange(_metric);

        return _metric;
    }

    const totalSales = useMemo(() => {
        return getMetricByPolkSalesWorkflowSet('polk_sales', 'salesCount', 'number');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [polkSalesWorkflowSets, previousPolkSalesWorkflowSets]);

    const attributedSales = useMemo(() => {
        return getMetricByPolkSalesWorkflowSet('polk_sales', 'distinctUserCount', 'number');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [polkSalesWorkflowSets, previousPolkSalesWorkflowSets]);

    const totalMarketShares = useMemo(() => {
        let _metric: Metric = {
            format: 'number',
            value: 0,
        };

        const salesWorlkflowMetric = getMetricByPolkSalesWorkflowSet('polk_sales', 'salesCount', 'number');
        const opportunityWorlkflowMetric = getMetricByPolkSalesWorkflowSet(
            'polk_opportunities',
            'salesCount',
            'number'
        );

        _metric.value = (salesWorlkflowMetric.value as number) + (opportunityWorlkflowMetric.value as number);
        _metric.previousValue =
            (salesWorlkflowMetric.previousValue as number) + (opportunityWorlkflowMetric.previousValue as number);

        _metric = setupMetricChange(_metric);

        return _metric;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [polkSalesWorkflowSets, previousPolkSalesWorkflowSets]);

    const totalAdShares = useMemo(() => {
        let _metric: Metric = {
            format: 'number',
            value: 0,
        };

        const salesWorlkflowMetric = getMetricByPolkSalesWorkflowSet('polk_sales', 'distinctUserCount', 'number');

        const opportunityWorlkflowMetric = getMetricByPolkSalesWorkflowSet(
            'polk_opportunities',
            'distinctUserCount',
            'number'
        );

        _metric.value = (salesWorlkflowMetric.value as number) + (opportunityWorlkflowMetric.value as number);
        _metric.previousValue =
            (salesWorlkflowMetric.previousValue as number) + (opportunityWorlkflowMetric.previousValue as number);

        _metric = setupMetricChange(_metric);

        return _metric;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [polkSalesWorkflowSets, previousPolkSalesWorkflowSets]);

    const totalImpressions: Metric = useMemo(() => {
        return getMetricByCampaignPerformance('impressions', 'number');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [performances, previousPerformances]);

    const totalClickthroughs: Metric = useMemo(() => {
        return getMetricByCampaignPerformance('clickthroughs', 'number');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [performances, previousPerformances]);

    const totalDailyReach: Metric = useMemo(() => {
        return setupMetricChange({
            format: 'number',
            value: amcReachAndFrequencyList
                .map((amcReachAndFrequency: any) => amcReachAndFrequency?.reach as number)
                .reduce((a: number, b: number) => a + b, 0),
            previousValue: previousAmcReachAndFrequencyList
                .map((amcReachAndFrequency: any) => amcReachAndFrequency?.reach as number)
                .reduce((a: number, b: number) => a + b, 0),
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [amcReachAndFrequencyList, previousAmcReachAndFrequencyList]);

    const totalImpressionFrequency: Metric = useMemo(() => {
        let _metric: Metric = {
            format: 'number',
            value: 0,
        };

        if (amcReachAndFrequencyList.length > 0) {
            _metric.value = amcReachAndFrequencyList
                .map((amcReachAndFrequency: any) => amcReachAndFrequency?.frequency as number)
                .reduce((a: number, b: number) => a + b, 0);
            _metric.value = Math.round(_metric.value / amcReachAndFrequencyList.length);
        }

        if (previousAmcReachAndFrequencyList.length > 0) {
            _metric.previousValue = previousAmcReachAndFrequencyList
                .map((amcReachAndFrequency: any) => amcReachAndFrequency?.frequency as number)
                .reduce((a: number, b: number) => a + b, 0);
            _metric.previousValue = Math.round(_metric.previousValue / previousAmcReachAndFrequencyList.length);
        }

        _metric = setupMetricChange(_metric);

        return _metric;
    }, [amcReachAndFrequencyList, previousAmcReachAndFrequencyList]);

    // const totalSpendMetricLabel: string = useMemo(() => {
    //     if (attributionDate) {
    //         return `${moment(attributionDate).format('MMMM')} Investment`;
    //     }

    //     return 'Total Investment';
    // }, [attributionDate]);

    // const costPerSoldTooltip: string = useMemo(() => {
    //     if (attributionDate) {
    //         return '{month} Investment divided by Total Ad Exposed Sales'.replace(
    //             '{month}',
    //             moment(attributionDate).format('MMMM')
    //         );
    //     }

    //     return 'Total Investment divided by Total Ad Exposed Sales';
    // }, [attributionDate]);

    useEffect(() => {
        setAttributionMetrics({
            ...attributionMetrics,
            ...{
                totalSales,
                attributedSales,
                // totalSpend,
                // costPerSold,
                totalImpressions,
                totalClickthroughs,
                totalImpressionFrequency,
                totalDailyReach,
                totalMarketShares,
                totalAdShares,
            },
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        totalSales,
        attributedSales,
        // totalSpend,
        // costPerSold,
        totalImpressions,
        totalClickthroughs,
        totalImpressionFrequency,
        totalDailyReach,
        totalMarketShares,
        totalAdShares,
    ]);

    return (
        <Grid container spacing={2}>
            <Grid size={4}>
                <MetricCard
                    loading={isLoadingPolkSalesWorkflowSets || isLoadingPreviousPolkSalesWorkflowSets}
                    metric={totalSales}
                    label="Total Sales"
                    tooltip="Total advertiser sales as reported by Polk by S&P Mobility"
                />
            </Grid>
            <Grid size={4}>
                <MetricCard
                    loading={isLoadingPolkSalesWorkflowSets || isLoadingPreviousPolkSalesWorkflowSets}
                    metric={attributedSales}
                    label="Total Ad Exposed Sales"
                    tooltip="Total households that were exposed to an advertisement and made a purchase for the selected time period"
                />
            </Grid>
            {/*<Grid item xs={3}>
                <MetricCard
                    metric={totalSpend}
                    label={totalSpendMetricLabel}
                    tooltip="Total advertiser ad spend for the selected time period"
                />
            </Grid>*/}
            {/*<Grid item xs={3}>
                <MetricCard
                    metric={costPerSold}
                    label="Cost Per Sale"
                    tooltip={costPerSoldTooltip}
                />
            </Grid>*/}
            <Grid size={4}>
                <MetricCard
                    loading={isFetchingPerformances || isFetchingPreviousPerformances}
                    metric={totalImpressions}
                    label="Total Exposures"
                    tooltip="Total number of ad exposures or impressions for the selected advertiser for the selected time period"
                />
            </Grid>
            <Grid size={4}>
                <MetricCard
                    loading={isFetchingPerformances || isFetchingPreviousPerformances}
                    metric={totalClickthroughs}
                    label="Total Clicks"
                    tooltip="Total number of clicks on an advertisement for the selected time period"
                />
            </Grid>
            <Grid size={4}>
                <MetricCard
                    loading={isFetchingAmcReachAndFrequencyList || isFetchingPreviousAmcReachAndFrequencyList}
                    metric={totalDailyReach}
                    label="Household Reach"
                    tooltip="Total number of households exposed to an advertisement"
                />
            </Grid>
            <Grid size={4}>
                <MetricCard
                    loading={isFetchingAmcReachAndFrequencyList || isFetchingPreviousAmcReachAndFrequencyList}
                    metric={totalImpressionFrequency}
                    label="Avg. Household Frequency"
                    tooltip="Avg. number of times a household saw advertisements across all media types and channels"
                    inverseColor={true}
                />
            </Grid>
        </Grid>
    );
}
